import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import { FORMATIONS_COLLECTION, firestore } from "../lib/firebase";
import type {
  Deck,
  DeckDraft,
  DeckVersion,
  Exercise,
  ExerciseDraft,
  ExerciseResponseMode,
  ExerciseVersion,
  PedagogicalContentKind,
  PedagogicalContentLists,
  PedagogicalVersion,
  Questionnaire,
  QuestionnaireDraft,
  QuestionnaireVersion,
} from "../types/pedagogicalContent";
import { asRecord, toDate } from "../utils/firestoreData";
import {
  commitFormationPublicationSnapshot,
  contentPublicationTagName,
} from "./formationContentVersioningService";
import { writeAuditEvent } from "./auditService";

const DECKS_COLLECTION = "decks";
const QUESTIONNAIRES_COLLECTION = "questionnaires";
const EXERCISES_COLLECTION = "exercises";
const VERSIONS_COLLECTION = "versions";

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function normalizeResponseMode(value: unknown): ExerciseResponseMode {
  return value === "markdown" ? "markdown" : "none";
}

function normalizeOptionalString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeDeck(id: string, data: Record<string, unknown>): Deck {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    createdAt: toDate(data.createdAt),
    currentPublishedVersionId:
      typeof data.currentPublishedVersionId === "string"
        ? data.currentPublishedVersionId
        : null,
    deletedAt: toDate(data.deletedAt),
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : null,
    draftContent: normalizeOptionalString(data.draftContent),
    exerciseRefs: normalizeStringArray(data.exerciseRefs),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    pageCount: typeof data.pageCount === "number" ? data.pageCount : 0,
    questionnaireRefs: normalizeStringArray(data.questionnaireRefs),
    title: normalizeOptionalString(data.title) || "Deck sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeDeckVersion(
  id: string,
  data: Record<string, unknown>,
): DeckVersion {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    createdAt: toDate(data.createdAt),
    deckId: normalizeOptionalString(data.deckId),
    exerciseRefs: normalizeStringArray(data.exerciseRefs),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    message: normalizeOptionalString(data.message),
    pageCount: typeof data.pageCount === "number" ? data.pageCount : 0,
    questionnaireRefs: normalizeStringArray(data.questionnaireRefs),
    title: normalizeOptionalString(data.title) || "Version deck",
    yamlContent: normalizeOptionalString(data.yamlContent),
  };
}

function normalizeQuestionnaire(
  id: string,
  data: Record<string, unknown>,
): Questionnaire {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    commentedCorrectAnswer: normalizeOptionalString(data.commentedCorrectAnswer),
    correctionDeckFragment: normalizeOptionalString(data.correctionDeckFragment),
    createdAt: toDate(data.createdAt),
    currentPublishedVersionId:
      typeof data.currentPublishedVersionId === "string"
        ? data.currentPublishedVersionId
        : null,
    deletedAt: toDate(data.deletedAt),
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : null,
    draftDefinition: normalizeOptionalString(data.draftDefinition),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    title: normalizeOptionalString(data.title) || "Questionnaire sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeQuestionnaireVersion(
  id: string,
  data: Record<string, unknown>,
): QuestionnaireVersion {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    commentedCorrectAnswer: normalizeOptionalString(data.commentedCorrectAnswer),
    correctionDeckFragment: normalizeOptionalString(data.correctionDeckFragment),
    createdAt: toDate(data.createdAt),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    message: normalizeOptionalString(data.message),
    questionnaireId: normalizeOptionalString(data.questionnaireId),
    title: normalizeOptionalString(data.title) || "Version questionnaire",
    yamlDefinition: normalizeOptionalString(data.yamlDefinition),
  };
}

function normalizeExercise(id: string, data: Record<string, unknown>): Exercise {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    correctionDeckFragment: normalizeOptionalString(data.correctionDeckFragment),
    correctionMarkdown: normalizeOptionalString(data.correctionMarkdown),
    createdAt: toDate(data.createdAt),
    currentPublishedVersionId:
      typeof data.currentPublishedVersionId === "string"
        ? data.currentPublishedVersionId
        : null,
    deletedAt: toDate(data.deletedAt),
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : null,
    draftStatement: normalizeOptionalString(data.draftStatement),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    responseMode: normalizeResponseMode(data.responseMode),
    title: normalizeOptionalString(data.title) || "Exercice sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeExerciseVersion(
  id: string,
  data: Record<string, unknown>,
): ExerciseVersion {
  return {
    authorUid: normalizeOptionalString(data.authorUid),
    correctionDeckFragment: normalizeOptionalString(data.correctionDeckFragment),
    correctionMarkdown: normalizeOptionalString(data.correctionMarkdown),
    createdAt: toDate(data.createdAt),
    exerciseId: normalizeOptionalString(data.exerciseId),
    formationId: normalizeOptionalString(data.formationId),
    id: normalizeOptionalString(data.id) || id,
    message: normalizeOptionalString(data.message),
    responseMode: normalizeResponseMode(data.responseMode),
    statementMarkdown: normalizeOptionalString(data.statementMarkdown),
    title: normalizeOptionalString(data.title) || "Version exercice",
  };
}

function createConverter<T>(
  normalize: (id: string, data: Record<string, unknown>) => T,
): FirestoreDataConverter<T> {
  return {
    fromFirestore(
      snapshot: QueryDocumentSnapshot<DocumentData>,
      options: SnapshotOptions,
    ): T {
      return normalize(snapshot.id, asRecord(snapshot.data(options)));
  },
  toFirestore(value: T): DocumentData {
    return value as DocumentData;
  },
};
}

const deckConverter = createConverter(normalizeDeck);
const deckVersionConverter = createConverter(normalizeDeckVersion);
const questionnaireConverter = createConverter(normalizeQuestionnaire);
const questionnaireVersionConverter = createConverter(
  normalizeQuestionnaireVersion,
);
const exerciseConverter = createConverter(normalizeExercise);
const exerciseVersionConverter = createConverter(normalizeExerciseVersion);

function collectionName(kind: PedagogicalContentKind): string {
  if (kind === "questionnaire") {
    return QUESTIONNAIRES_COLLECTION;
  }

  if (kind === "exercise") {
    return EXERCISES_COLLECTION;
  }

  return DECKS_COLLECTION;
}

function contentCollectionRef(formationId: string, kind: PedagogicalContentKind) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return collection(
    firestore,
    FORMATIONS_COLLECTION,
    formationId,
    collectionName(kind),
  );
}

function contentDocRef(
  formationId: string,
  kind: PedagogicalContentKind,
  contentId: string,
) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    FORMATIONS_COLLECTION,
    formationId,
    collectionName(kind),
    contentId,
  );
}

function versionCollectionRef(
  formationId: string,
  kind: PedagogicalContentKind,
  contentId: string,
) {
  return collection(
    contentDocRef(formationId, kind, contentId),
    VERSIONS_COLLECTION,
  );
}

function assertTitle(title: string): string {
  const normalizedTitle = title.trim();

  if (!normalizedTitle) {
    throw new Error("Le titre est obligatoire.");
  }

  return normalizedTitle;
}

function requireData<T>(value: T | undefined, label: string): T {
  if (!value) {
    throw new Error(`${label} introuvable après écriture.`);
  }

  return value;
}

function activeOnly<T extends { deletedAt: Date | null }>(items: T[]): T[] {
  return items.filter((item) => item.deletedAt === null);
}

export async function listPedagogicalContent(
  formationId: string,
): Promise<PedagogicalContentLists> {
  if (!firestore) {
    return {
      decks: [],
      exercises: [],
      questionnaires: [],
    };
  }

  const [decksSnapshot, questionnairesSnapshot, exercisesSnapshot] =
    await Promise.all([
      getDocs(
        query(
          contentCollectionRef(formationId, "deck").withConverter(deckConverter),
          orderBy("updatedAt", "desc"),
        ),
      ),
      getDocs(
        query(
          contentCollectionRef(formationId, "questionnaire").withConverter(
            questionnaireConverter,
          ),
          orderBy("updatedAt", "desc"),
        ),
      ),
      getDocs(
        query(
          contentCollectionRef(formationId, "exercise").withConverter(
            exerciseConverter,
          ),
          orderBy("updatedAt", "desc"),
        ),
      ),
    ]);

  return {
    decks: activeOnly(decksSnapshot.docs.map((deckDoc) => deckDoc.data())),
    exercises: activeOnly(
      exercisesSnapshot.docs.map((exerciseDoc) => exerciseDoc.data()),
    ),
    questionnaires: activeOnly(
      questionnairesSnapshot.docs.map((questionnaireDoc) =>
        questionnaireDoc.data(),
      ),
    ),
  };
}

export async function createDeck({
  actorUid,
  draft,
  formationId,
}: {
  actorUid: string;
  draft: DeckDraft;
  formationId: string;
}): Promise<Deck> {
  const title = assertTitle(draft.title);
  const deckRef = doc(contentCollectionRef(formationId, "deck"));
  const deckId = deckRef.id;

  await setDoc(deckRef, {
    authorUid: actorUid,
    createdAt: serverTimestamp(),
    draftContent: draft.draftContent,
    exerciseRefs: draft.exerciseRefs,
    formationId,
    id: deckId,
    pageCount: Math.max(0, Math.trunc(draft.pageCount)),
    questionnaireRefs: draft.questionnaireRefs,
    title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "deck.created",
    metadata: {
      deckId,
      formationId,
    },
  });

  const snapshot = await getDoc(deckRef.withConverter(deckConverter));
  return requireData(snapshot.data(), "Deck");
}

export async function updateDeck({
  actorUid,
  deckId,
  draft,
  formationId,
}: {
  actorUid: string;
  deckId: string;
  draft: DeckDraft;
  formationId: string;
}): Promise<Deck> {
  const title = assertTitle(draft.title);

  await updateDoc(contentDocRef(formationId, "deck", deckId), {
    draftContent: draft.draftContent,
    exerciseRefs: draft.exerciseRefs,
    pageCount: Math.max(0, Math.trunc(draft.pageCount)),
    questionnaireRefs: draft.questionnaireRefs,
    title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "deck.updated",
    metadata: {
      deckId,
      formationId,
    },
  });

  const snapshot = await getDoc(
    contentDocRef(formationId, "deck", deckId).withConverter(deckConverter),
  );
  return requireData(snapshot.data(), "Deck");
}

export async function publishDeckVersion({
  actorUid,
  deck,
  message,
}: {
  actorUid: string;
  deck: Deck;
  message: string;
}): Promise<DeckVersion> {
  const versionRef = doc(versionCollectionRef(deck.formationId, "deck", deck.id));
  const versionId = versionRef.id;

  await setDoc(versionRef, {
    authorUid: actorUid,
    createdAt: serverTimestamp(),
    deckId: deck.id,
    exerciseRefs: deck.exerciseRefs,
    formationId: deck.formationId,
    id: versionId,
    message: message.trim(),
    pageCount: deck.pageCount,
    questionnaireRefs: deck.questionnaireRefs,
    title: deck.title,
    yamlContent: deck.draftContent,
  });
  await updateDoc(contentDocRef(deck.formationId, "deck", deck.id), {
    currentPublishedVersionId: versionId,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "deck.version.published",
    metadata: {
      deckId: deck.id,
      formationId: deck.formationId,
      versionId,
    },
  });
  await commitFormationPublicationSnapshot({
    actorUid,
    content: await listPedagogicalContent(deck.formationId),
    formationId: deck.formationId,
    message: message.trim() || `Publication deck ${deck.title}`,
    tagName: contentPublicationTagName({
      contentId: deck.id,
      kind: "deck",
      versionId,
    }),
  });

  const snapshot = await getDoc(versionRef.withConverter(deckVersionConverter));
  return requireData(snapshot.data(), "Version deck");
}

export async function createQuestionnaire({
  actorUid,
  draft,
  formationId,
}: {
  actorUid: string;
  draft: QuestionnaireDraft;
  formationId: string;
}): Promise<Questionnaire> {
  const title = assertTitle(draft.title);
  const questionnaireRef = doc(
    contentCollectionRef(formationId, "questionnaire"),
  );
  const questionnaireId = questionnaireRef.id;

  await setDoc(questionnaireRef, {
    authorUid: actorUid,
    commentedCorrectAnswer: draft.commentedCorrectAnswer,
    correctionDeckFragment: draft.correctionDeckFragment,
    createdAt: serverTimestamp(),
    draftDefinition: draft.draftDefinition,
    formationId,
    id: questionnaireId,
    title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "questionnaire.created",
    metadata: {
      formationId,
      questionnaireId,
    },
  });

  const snapshot = await getDoc(
    questionnaireRef.withConverter(questionnaireConverter),
  );
  return requireData(snapshot.data(), "Questionnaire");
}

export async function updateQuestionnaire({
  actorUid,
  draft,
  formationId,
  questionnaireId,
}: {
  actorUid: string;
  draft: QuestionnaireDraft;
  formationId: string;
  questionnaireId: string;
}): Promise<Questionnaire> {
  const title = assertTitle(draft.title);

  await updateDoc(
    contentDocRef(formationId, "questionnaire", questionnaireId),
    {
      commentedCorrectAnswer: draft.commentedCorrectAnswer,
      correctionDeckFragment: draft.correctionDeckFragment,
      draftDefinition: draft.draftDefinition,
      title,
      updatedAt: serverTimestamp(),
    },
  );
  await writeAuditEvent({
    actorUid,
    eventType: "questionnaire.updated",
    metadata: {
      formationId,
      questionnaireId,
    },
  });

  const snapshot = await getDoc(
    contentDocRef(formationId, "questionnaire", questionnaireId).withConverter(
      questionnaireConverter,
    ),
  );
  return requireData(snapshot.data(), "Questionnaire");
}

export async function publishQuestionnaireVersion({
  actorUid,
  message,
  questionnaire,
}: {
  actorUid: string;
  message: string;
  questionnaire: Questionnaire;
}): Promise<QuestionnaireVersion> {
  const versionRef = doc(
    versionCollectionRef(
      questionnaire.formationId,
      "questionnaire",
      questionnaire.id,
    ),
  );
  const versionId = versionRef.id;

  await setDoc(versionRef, {
    authorUid: actorUid,
    commentedCorrectAnswer: questionnaire.commentedCorrectAnswer,
    correctionDeckFragment: questionnaire.correctionDeckFragment,
    createdAt: serverTimestamp(),
    formationId: questionnaire.formationId,
    id: versionId,
    message: message.trim(),
    questionnaireId: questionnaire.id,
    title: questionnaire.title,
    yamlDefinition: questionnaire.draftDefinition,
  });
  await updateDoc(
    contentDocRef(questionnaire.formationId, "questionnaire", questionnaire.id),
    {
      currentPublishedVersionId: versionId,
      updatedAt: serverTimestamp(),
    },
  );
  await writeAuditEvent({
    actorUid,
    eventType: "questionnaire.version.published",
    metadata: {
      formationId: questionnaire.formationId,
      questionnaireId: questionnaire.id,
      versionId,
    },
  });
  await commitFormationPublicationSnapshot({
    actorUid,
    content: await listPedagogicalContent(questionnaire.formationId),
    formationId: questionnaire.formationId,
    message: message.trim() || `Publication questionnaire ${questionnaire.title}`,
    tagName: contentPublicationTagName({
      contentId: questionnaire.id,
      kind: "questionnaire",
      versionId,
    }),
  });

  const snapshot = await getDoc(
    versionRef.withConverter(questionnaireVersionConverter),
  );
  return requireData(snapshot.data(), "Version questionnaire");
}

export async function createExercise({
  actorUid,
  draft,
  formationId,
}: {
  actorUid: string;
  draft: ExerciseDraft;
  formationId: string;
}): Promise<Exercise> {
  const title = assertTitle(draft.title);
  const exerciseRef = doc(contentCollectionRef(formationId, "exercise"));
  const exerciseId = exerciseRef.id;

  await setDoc(exerciseRef, {
    authorUid: actorUid,
    correctionDeckFragment: draft.correctionDeckFragment,
    correctionMarkdown: draft.correctionMarkdown,
    createdAt: serverTimestamp(),
    draftStatement: draft.draftStatement,
    formationId,
    id: exerciseId,
    responseMode: draft.responseMode,
    title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "exercise.created",
    metadata: {
      exerciseId,
      formationId,
    },
  });

  const snapshot = await getDoc(exerciseRef.withConverter(exerciseConverter));
  return requireData(snapshot.data(), "Exercice");
}

export async function updateExercise({
  actorUid,
  draft,
  exerciseId,
  formationId,
}: {
  actorUid: string;
  draft: ExerciseDraft;
  exerciseId: string;
  formationId: string;
}): Promise<Exercise> {
  const title = assertTitle(draft.title);

  await updateDoc(contentDocRef(formationId, "exercise", exerciseId), {
    correctionDeckFragment: draft.correctionDeckFragment,
    correctionMarkdown: draft.correctionMarkdown,
    draftStatement: draft.draftStatement,
    responseMode: draft.responseMode,
    title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "exercise.updated",
    metadata: {
      exerciseId,
      formationId,
    },
  });

  const snapshot = await getDoc(
    contentDocRef(formationId, "exercise", exerciseId).withConverter(
      exerciseConverter,
    ),
  );
  return requireData(snapshot.data(), "Exercice");
}

export async function publishExerciseVersion({
  actorUid,
  exercise,
  message,
}: {
  actorUid: string;
  exercise: Exercise;
  message: string;
}): Promise<ExerciseVersion> {
  const versionRef = doc(
    versionCollectionRef(exercise.formationId, "exercise", exercise.id),
  );
  const versionId = versionRef.id;

  await setDoc(versionRef, {
    authorUid: actorUid,
    correctionDeckFragment: exercise.correctionDeckFragment,
    correctionMarkdown: exercise.correctionMarkdown,
    createdAt: serverTimestamp(),
    exerciseId: exercise.id,
    formationId: exercise.formationId,
    id: versionId,
    message: message.trim(),
    responseMode: exercise.responseMode,
    statementMarkdown: exercise.draftStatement,
    title: exercise.title,
  });
  await updateDoc(
    contentDocRef(exercise.formationId, "exercise", exercise.id),
    {
      currentPublishedVersionId: versionId,
      updatedAt: serverTimestamp(),
    },
  );
  await writeAuditEvent({
    actorUid,
    eventType: "exercise.version.published",
    metadata: {
      exerciseId: exercise.id,
      formationId: exercise.formationId,
      versionId,
    },
  });
  await commitFormationPublicationSnapshot({
    actorUid,
    content: await listPedagogicalContent(exercise.formationId),
    formationId: exercise.formationId,
    message: message.trim() || `Publication exercice ${exercise.title}`,
    tagName: contentPublicationTagName({
      contentId: exercise.id,
      kind: "exercise",
      versionId,
    }),
  });

  const snapshot = await getDoc(
    versionRef.withConverter(exerciseVersionConverter),
  );
  return requireData(snapshot.data(), "Version exercice");
}

export async function softDeletePedagogicalContent({
  actorUid,
  contentId,
  formationId,
  kind,
}: {
  actorUid: string;
  contentId: string;
  formationId: string;
  kind: PedagogicalContentKind;
}): Promise<void> {
  await updateDoc(contentDocRef(formationId, kind, contentId), {
    deletedAt: serverTimestamp(),
    deletedBy: actorUid,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: `${kind}.deleted`,
    metadata: {
      contentId,
      formationId,
    },
  });
}

export async function listPedagogicalVersions({
  contentId,
  formationId,
  kind,
}: {
  contentId: string;
  formationId: string;
  kind: PedagogicalContentKind;
}): Promise<PedagogicalVersion[]> {
  if (!firestore) {
    return [];
  }

  const versionsQuery = query(
    versionCollectionRef(formationId, kind, contentId),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(versionsQuery);

  if (kind === "questionnaire") {
    return snapshot.docs.map((versionDoc) =>
      normalizeQuestionnaireVersion(versionDoc.id, asRecord(versionDoc.data())),
    );
  }

  if (kind === "exercise") {
    return snapshot.docs.map((versionDoc) =>
      normalizeExerciseVersion(versionDoc.id, asRecord(versionDoc.data())),
    );
  }

  return snapshot.docs.map((versionDoc) =>
    normalizeDeckVersion(versionDoc.id, asRecord(versionDoc.data())),
  );
}
